import {
  AfterViewInit,
  ApplicationRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { Plant } from "src/model/class/general classes/plant";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { EventService } from "src/service/event.service";
import { listPlant } from "src/state/selectors/plant.selectors";
import Swiper from "swiper";

@Component({
    selector: "app-plant-list",
    templateUrl: "./plant-list.component.html",
    styleUrls: ["./plant-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlantListComponent
  implements OnInit, AfterViewInit, OnDestroy, iEvent, iTranslation {
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectListPlantSub: Subscription = null;

  //#endregion

  private _translateSubscription: Subscription = null;
  private _isSlidList: boolean = false;
  private _plantListPageSwiper: Swiper = null;

  public listPlants: Array<Plant> = new Array();
  public lblNoPlant: string = "Test no plant";
  public _eventSubscriptions: Array<Subscription> = null;
  public plantSearchText: string = "";
  public lblSearchPlantsPlaceholder: string = "";
  public isApp: boolean = false;
  public isSwiperVisible: boolean = false;

  @Input() currentValueTab: number;
  @Output() checkSlidingLocal = new EventEmitter<Boolean>();
  @Output() clickPlantListEllipsisIcon = new EventEmitter<Event>();

  constructor(
    private _eventService: EventService,
    private _app: ApplicationRef,
    private _store: Store,
    private _ngZone: NgZone,
    private _translateService: TranslateService,
    private _platform: Platform
  ) {
    this.getMobileCondition();
  }

  ngOnInit() {
    this.getMobileCondition();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.loadPlantList();

    this.plantSearchText = "";
    this.initializePlantListSlider();
  }

  ngAfterViewInit(): void {
    this.initializePlantListSlider();
  }

  ionViewCanLeave() {
    this._plantListPageSwiper?.destroy(true);
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }
  }

  /**
   * Check if an element has been slid, the value is output to the parent component after 200ms
   * to avoid the button appearing/disappearing in case an element
   * is slid even though there is already one open
   * @param value
   */
  public checkSliding(value) {
    this._isSlidList = value;
    setTimeout(() => {
      this.checkSlidingLocal.emit(this._isSlidList);
    }, 300);
  }

  //#region  Translation function
  /**
   * Handle native translation event
   */
  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }

  /**
   * Load translated strings
   */
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.noPlant", "dictionary.searchPlantList"])
      .subscribe((translatedStrings) => {
        this._ngZone.run(() => {
          this.lblNoPlant = translatedStrings["dictionary.noPlant"];
          this.lblSearchPlantsPlaceholder =
            translatedStrings["dictionary.searchPlantList"];
        });
      });
  }
  //#endregion

  //#region Subscribe to event-service observable ///
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnQRPlantScanned.subscribe((plant: Plant) => {
        plant.LanPort = "80";
        plant.WanPort = "80";

        this._app.tick();
      })
    );
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Load plant list
   */
  private loadPlantList(): void {
    this._selectListPlantSub = this._store
      .select(listPlant)
      .subscribe((listPlant: Array<Plant>) => {
        this._ngZone.run(() => {
          this.listPlants = listPlant;
        });
      });
  }

  /**
   * This emits a click event to the parent component in order to open the plant list page drop down menu
   * @param event Event
   */
  public onClickPlantListEllipsisIcon(event: Event) {
    this.clickPlantListEllipsisIcon.emit(event);
  }
  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectListPlantSub) {
      this._selectListPlantSub.unsubscribe();
    }
  }

  //#endregion

  /**
   * Initialize plant list page swiper only if there is no plant and isApp mode
   */
  private initializePlantListSlider(): void {
    if (this.isApp) {
      this.isSwiperVisible = true;
      this._plantListPageSwiper = this.initializeSwiper(
        ".plant-list-swiper-container"
      );

      this._plantListPageSwiper.updateSlides();
      this._plantListPageSwiper.updateSlidesClasses();
    }
  }

  /**
   * Create an instance of the Swiper class
   * @param swiperClass the class to apply swiper on
   * @returns Swiper
   */
  private initializeSwiper(swiperClass: string): Swiper {
    return new Swiper(swiperClass, {
      direction: "horizontal",
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      initialSlide: 0,
      speed: 300,
    });
  }
  /**
   * Check if the Platform is ios or andriod (App mode)
   * This is needed to know when the platform is in mobile form
   */
  private getMobileCondition(): void {
    this.isApp = this._platform.is("ios") || this._platform.is("android");
  }
}
